import {
  ACCOUNT_TYPES,
  CHILEAN_BANKS,
  formatRutInput,
  validateRutInput,
} from '@/lib';
import { createBankAccount, updateBankAccount } from '@/lib/recepthor-api';
import {
  Button,
  Checkbox,
  Field,
  HelperText,
  Input,
  Modal,
  Select,
} from '@/ui';
import PropTypes from 'prop-types';
import { useState } from 'react';

import '@/shared/OrgModales/ModalForm.css';

// Chilean account numbers are digit strings whose length varies by bank;
// enforce numeric-only with a sane length rather than a bank-specific rule.
const ACCOUNT_NUMBER_RE = /^\d{4,20}$/;

// Pure so both the submit path and the live re-validation effect share it.
function validateForm(form) {
  const next = {};
  if (!form.bankCode) next.bankCode = 'Selecciona un banco.';
  if (!form.accountType) next.accountType = 'Selecciona el tipo de cuenta.';
  if (!form.holderName.trim()) next.holderName = 'Ingresa el titular.';

  const rutError = validateRutInput(form.rut.trim());
  if (rutError) next.rut = rutError;

  const accNumber = form.accountNumber.replace(/\D/g, '');
  if (!accNumber) next.accountNumber = 'Ingresa el número de cuenta.';
  else if (!ACCOUNT_NUMBER_RE.test(accNumber))
    next.accountNumber = 'Debe tener entre 4 y 20 dígitos.';

  return next;
}

export default function EditarCuentaBancariaModal({
  account,
  onClose,
  onSaved,
  flat = false,
}) {
  const isEdit = Boolean(account?.bankAccountId);

  const [form, setForm] = useState({
    bankCode: account?.bankCode ?? '',
    accountType: account?.accountType ?? '',
    holderName: account?.holderName ?? '',
    accountNumber: account?.accountNumber ?? '',
    rut: account?.rut ?? '',
    isDefault: account?.isDefault ?? false,
  });
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Field errors are derived, never stored: a field validates from its first
  // keystroke, so correcting it clears the message with no submit involved.
  // Untouched fields stay clean until a blocked submit reveals every one.
  const allErrors = validateForm(form);
  const errors = submitted
    ? allErrors
    : Object.fromEntries(
        Object.entries(allErrors).filter(([key]) => touched[key]),
      );

  const markTouched = (key) =>
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  const setField = (key) => (e) => {
    markTouched(key);
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  async function handleSubmit(e) {
    e?.preventDefault();
    setError('');
    setSubmitted(true);
    // `submitted` above already reveals every error through `errors`.
    if (Object.keys(allErrors).length > 0) return;

    const body = {
      bankCode: form.bankCode,
      accountType: form.accountType,
      holderName: form.holderName.trim(),
      accountNumber: form.accountNumber.replace(/\D/g, ''),
      // Normalized (dots stripped, dash ensured): Enter-submit skips the
      // onBlur formatting pass, so the raw typed value could reach the API.
      rut: formatRutInput(form.rut.trim()),
    };

    setLoading(true);
    try {
      if (isEdit) {
        await updateBankAccount(account.bankAccountId, {
          ...body,
          isDefault: form.isDefault,
        });
      } else {
        await createBankAccount(body);
      }
      await onSaved?.();
    } catch {
      setError('No se pudo guardar. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open onClose={onClose} flat={flat}>
      <Modal.Header
        title={isEdit ? 'Editar cuenta bancaria' : 'Agregar cuenta bancaria'}
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="cfg-form">
            <Field label="Banco" error={errors.bankCode}>
              <Select
                placeholder="Selecciona un banco…"
                value={form.bankCode}
                onChange={setField('bankCode')}
                error={!!errors.bankCode}
              >
                {CHILEAN_BANKS.map((b) => (
                  <option key={b.code} value={b.code}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Tipo de cuenta" error={errors.accountType}>
              <Select
                placeholder="Selecciona el tipo…"
                value={form.accountType}
                onChange={setField('accountType')}
                error={!!errors.accountType}
              >
                {ACCOUNT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Titular" error={errors.holderName}>
              <Input
                value={form.holderName}
                onChange={setField('holderName')}
                error={!!errors.holderName}
              />
            </Field>

            <Field label="RUT del titular" error={errors.rut}>
              <Input
                variant="mono"
                value={form.rut}
                onChange={setField('rut')}
                onBlur={() =>
                  setForm((prev) => ({
                    ...prev,
                    rut: formatRutInput(prev.rut),
                  }))
                }
                error={!!errors.rut}
                placeholder="12.345.678-9"
              />
            </Field>

            <Field label="Número de cuenta" error={errors.accountNumber} full>
              <Input
                variant="mono"
                inputMode="numeric"
                value={form.accountNumber}
                onChange={(e) => {
                  markTouched('accountNumber');
                  setForm((prev) => ({
                    ...prev,
                    accountNumber: e.target.value.replace(/\D/g, ''),
                  }));
                }}
                error={!!errors.accountNumber}
                placeholder="0001234567"
              />
            </Field>

            <div className="cfg-form__field cfg-form__field--full">
              <label className="cfg-form__check">
                <Checkbox
                  checked={form.isDefault}
                  onChange={() =>
                    setForm((prev) => ({ ...prev, isDefault: !prev.isDefault }))
                  }
                  aria-label="Usar como cuenta principal"
                />
                <span>Usar como cuenta principal</span>
              </label>
            </div>

            {error && (
              <div className="cfg-form__field--full">
                <HelperText kind="err">{error}</HelperText>
              </div>
            )}
          </div>
          <button
            type="submit"
            aria-hidden="true"
            style={{ display: 'none' }}
          />
        </Modal.Body>
      </form>
      <Modal.Foot>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="seal"
          type="button"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Guardando…' : 'Guardar'}
        </Button>
      </Modal.Foot>
    </Modal>
  );
}

EditarCuentaBancariaModal.propTypes = {
  account: PropTypes.shape({
    bankAccountId: PropTypes.string,
    bankCode: PropTypes.string,
    accountType: PropTypes.string,
    holderName: PropTypes.string,
    accountNumber: PropTypes.string,
    rut: PropTypes.string,
    isDefault: PropTypes.bool,
  }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  flat: PropTypes.bool,
};
